import { useState } from 'react'
import { Check, X, FileText, ShieldCheck } from 'lucide-react'
import { useLedger } from '../lib/LedgerContext'
import { formatDate } from '../lib/format'

const STATUS_META = {
  Approved: 'border-emerald-300 text-emerald-700 bg-emerald-50',
  Rejected: 'border-rose-300 text-rose-700 bg-rose-50',
  'Pending Review': 'border-amber-300 text-amber-700 bg-amber-50',
}

export default function DocumentApproval() {
  const { docQueue, approveDocument, rejectDocument } = useLedger()
  const [rejectingId, setRejectingId] = useState(null)
  const [reason, setReason] = useState('')

  const pending = docQueue.filter((d) => d.status === 'Pending Review')

  const submitReject = (id) => {
    rejectDocument(id, reason.trim() || 'Does not meet compliance requirements.')
    setRejectingId(null)
    setReason('')
  }

  return (
    <div className="surface">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-neutral-200">
        <ShieldCheck size={15} className="text-brand" />
        <div>
          <h3 className="text-sm font-semibold text-ink">Document Approval</h3>
          <p className="text-xs text-muted mt-0.5">
            {pending.length} pending review · approved documents move to the Legal screen.
          </p>
        </div>
      </div>

      <div className="divide-y divide-neutral-200">
        {docQueue.map((d) => (
          <div key={d.id} className="px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 border border-neutral-200 bg-neutral-50 flex items-center justify-center shrink-0">
                  <FileText size={15} className="text-neutral-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink">{d.name}</p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-neutral-500">
                    <span>{d.category}</span>
                    <span className="font-mono">{d.id}</span>
                    <span>by {d.uploadedBy}</span>
                    <span className="font-mono">{formatDate(d.uploadedAt)}</span>
                  </div>
                  {d.rejectReason && (
                    <p className="text-[11px] text-rose-600 mt-1.5">Reason: {d.rejectReason}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${STATUS_META[d.status]}`}>
                  {d.status}
                </span>
                {d.status === 'Pending Review' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveDocument(d.id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-brand hover:bg-brand-dark transition-colors"
                    >
                      <Check size={12} /> Approve
                    </button>
                    <button
                      onClick={() => setRejectingId(d.id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold border border-neutral-300 text-neutral-600 hover:border-rose-300 hover:text-rose-600 transition-colors"
                    >
                      <X size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>

            {rejectingId === d.id && (
              <div className="mt-3 pl-12 flex gap-2 animate-fade-in">
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="input-flat"
                  placeholder="Rejection reason (visible to staff)"
                  autoFocus
                />
                <button onClick={() => submitReject(d.id)} className="btn-primary text-xs">Submit</button>
                <button onClick={() => setRejectingId(null)} className="btn-secondary text-xs">Cancel</button>
              </div>
            )}
          </div>
        ))}
        {docQueue.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-muted">No documents submitted for review.</div>
        )}
      </div>
    </div>
  )
}
